/**
 * BasemapSelector — кнопка-бутерброд (Layers icon) с попапом из 3 опций
 * (Спутник / Гибрид / Карта) для переключения базовой подложки на /map.
 *
 * Поведение:
 *   - По умолчанию виден только icon button (компактно, не закрывает карту).
 *   - Click → раскрывается pill-group с 3 опциями.
 *   - Click outside / повторный click на icon → сворачивается.
 *   - Click на опцию → setBasemap(mode) + auto-close popup.
 *
 * Расположение: top-left под зум-кнопками (см. /map page layout).
 *
 * Spec: AK-36 v2 (founder feedback 2026-05-02 «спрятать под бутерброд»).
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Globe, Layers, Map as MapIcon } from 'lucide-react';
import { useMapStore, type Basemap } from '@/lib/map/useMapStore';
import { cn } from '@/lib/utils';

interface BasemapOption {
  value: Basemap;
  label: string;
  Icon: typeof Globe;
}

const OPTIONS: BasemapOption[] = [
  { value: 'satellite', label: 'Спутник', Icon: Globe },
  { value: 'hybrid', label: 'Гибрид', Icon: Layers },
  { value: 'streets', label: 'Карта', Icon: MapIcon },
];

interface BasemapSelectorProps {
  className?: string;
}

export function BasemapSelector({ className }: BasemapSelectorProps) {
  const basemap = useMapStore((s) => s.basemap);
  const setBasemap = useMapStore((s) => s.setBasemap);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click outside → close popup.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (target && containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSelect = (mode: Basemap) => {
    setBasemap(mode);
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={cn('pointer-events-auto absolute top-3 left-3 z-10', className)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Тип карты"
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="basemap-selector-toggle"
        className={cn(
          'h-9 w-9 flex items-center justify-center',
          'bg-white rounded-md shadow-md border',
          'text-slate-700 hover:bg-slate-50 transition-colors',
          open && 'bg-slate-50',
        )}
        title={`Тип карты: ${OPTIONS.find((o) => o.value === basemap)?.label ?? basemap}`}
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        <Layers size={16} strokeWidth={2} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Выбор типа карты"
          data-testid="basemap-selector-menu"
          className={cn(
            'absolute top-0 left-11',
            'bg-white rounded-md shadow-md border overflow-hidden',
            'flex flex-col text-xs font-medium',
          )}
          style={{ width: 132, borderColor: 'hsl(var(--border))' }}
        >
          {OPTIONS.map((opt, idx) => {
            const active = basemap === opt.value;
            const isFirst = idx === 0;
            const isLast = idx === OPTIONS.length - 1;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => handleSelect(opt.value)}
                data-testid={`basemap-option-${opt.value}`}
                className={cn(
                  'px-3 py-2 flex items-center gap-2 transition-colors',
                  !isFirst && 'border-t',
                  !isLast && 'border-b',
                  active
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-slate-700 hover:bg-slate-50',
                )}
                style={{ borderColor: 'hsl(var(--border))' }}
              >
                <opt.Icon size={14} strokeWidth={2} aria-hidden="true" />
                <span>{opt.label}</span>
                {active && (
                  <Check
                    size={14}
                    strokeWidth={3}
                    className="ml-auto"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
